import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import Button from '../../../util_components/button';
import CustomLoading from '../../../util_components/custom_loading';
import { api_with_method, get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { build_active_packages_url } from './create_membership_utils';

// The three tri-state columns are where this component kept slipping, so they are typed rather
// than described. On a stored row: null = "not scoped by this", 0 = a real stored value. They are
// not interchangeable — see scope_weeks_label for what a 0 no_of_weeks actually means.
type TriState = 0 | 1 | null;

// A student_override row as the list endpoint returns it.
interface OverrideRow {
  id: number;
  type: string;
  package_uuid: string | null;
  currency: string | null;
  is_prepaid: TriState;
  no_of_weeks: number | null;
  prepaid_members_uuid: string | null;
  valid_from: string | null;
  valid_until: string | null;
  amount: number | string | null;
  is_rollover_allowed: TriState;
  ticket: string;
  reason: string;
  is_active: 0 | 1;
}

// Everything a <select>/<input> hands back is a string, so the form holds strings end to end and
// '' is the single "not set" value. handle_submit is the one place '' becomes null and '1' becomes
// 1 — nothing else in here should be doing that conversion.
type TriInput = '' | '0' | '1';

interface FormState {
  student_uuid: string;
  type: string;
  package_uuid: string;
  currency: string;
  is_prepaid: TriInput;
  no_of_weeks: string;
  prepaid_members_uuid: string;
  valid_from: string;
  valid_until: string;
  amount: string;
  is_rollover_allowed: TriInput;
  ticket: string;
  reason: string;
}

// Only the slice of the student_details blob this panel reads.
interface SubscriptionInfo {
  is_prepaid?: number | string;
  is_high_volume?: number | string;
  plan_category?: string; // PROD-9606: legacy | consistency | consistency_recording
  prepaid_no_of_weeks?: number | string;
  prepaid_members_uuid?: string;
  prepaid_next_charge?: string;
  purchase_price?: number | string;
  currency?: string;
  currency_symbol?: string;
  my_account_membership?: { membership_renew_date?: string };
}

interface StudentDetails {
  subscription_info?: SubscriptionInfo;
  subscription_package_name?: string;
  next_transaction_date?: string;
  no_of_weeks_originally_enrolled_to?: number | string;
}

interface StudentOverridesProps {
  student_uuid: string;
  current_currency?: string;
  current_package_uuid?: string;
  student_details?: StudentDetails;
  on_close: () => void;
  set_notification_variable: (show: boolean, type: string, message: string) => void;
}

// Labels sit inside <label>, which a global stylesheet uppercases and bolds — normal-case undoes it.
const label_class = 'mb-1.5 block text-xs font-semibold normal-case text-content-secondary';
const field_class =
  'w-full rounded-lg border border-line bg-surface-card px-3 py-2 text-sm text-content-primary transition-colors focus:border-brand-primary focus:outline-none';
// Micro-label above a value (stat cells, group headings).
const stat_label_class = 'text-[11px] font-semibold uppercase tracking-wider text-content-muted';
const section_title_class = 'text-[11px] font-semibold uppercase tracking-wider text-content-muted';
const chip_class =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-content-secondary';
// common.css styles bare `button` (padding, brand background, border, 5px margin) via an element
// selector, which a single utility outranks — except `button:hover`, which is (0,1,1) and needs an
// explicit hover: utility to beat. That pair is why these are classes now and not inline styles.
const ghost_button_class =
  'm-0 border-0 bg-transparent p-0 text-xs font-medium shadow-none hover:bg-transparent disabled:opacity-50';

const format_date = (value?: string | null) => {
  if (!value) return '—';
  const date = moment(value);
  if (!date.isValid()) return value;
  return date.format('DD MMM YYYY');
};

const to_iso = (value?: string | null) => (value ? new Date(value).toISOString() : '');

const to_date_input = (value?: string | null) => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

const is_live = (override: OverrideRow) => Number(override?.is_active) === 1;

const type_label = (type?: string) =>
  String(type || '')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

// Monthly billing is a 4-week cycle (renewal_service.ts's `let no_of_weeks = 4` default).
const MONTHLY_CYCLE_WEEKS = 4;

// 0/absent means the row was stored without a bundle length, which reads two different ways:
//   is_prepaid 0 -> monthly, and monthly IS a 4-week cycle, so name it.
//   otherwise    -> a legacy row written before no_of_weeks existed. Its real cycle could be
//                   12/24/28/52, so say unscoped rather than claim 4 and be wrong.
const scope_weeks_label = (
  no_of_weeks: number | string | null,
  is_prepaid: TriState | TriInput
) => {
  const weeks = Number(no_of_weeks);
  if (weeks > 0) return `${weeks}-week cycle`;
  return Number(is_prepaid) === 0 ? `${MONTHLY_CYCLE_WEEKS}-week cycle` : 'any cycle length';
};

// NULL on a stored row means "any", not "unknown".
const scope_billing_label = (is_prepaid: TriState | TriInput) => {
  if (is_prepaid === null || is_prepaid === undefined || is_prepaid === '')
    return 'any billing mode';
  return Number(is_prepaid) === 1 ? 'prepaid' : 'monthly';
};

const initial_form_state = (student_uuid: string, is_prepaid: TriInput = ''): FormState => ({
  student_uuid,
  type: 'renewal',
  package_uuid: '',
  currency: '',
  is_prepaid,
  // Scope: a price agreed for a 12-week bundle must not be charged for a 24-week one.
  no_of_weeks: '',
  // Provenance only: which prepaid_members row the price was agreed against.
  prepaid_members_uuid: '',
  valid_from: '',
  valid_until: '',
  amount: '',
  is_rollover_allowed: '',
  ticket: '',
  reason: ''
});

function StudentOverrides({
  student_uuid,
  current_currency = '',
  current_package_uuid = '',
  student_details = {},
  on_close,
  set_notification_variable
}: StudentOverridesProps) {
  const [loading, set_loading] = useState(false);
  const [saving, set_saving] = useState(false);
  const [overrides, set_overrides] = useState<OverrideRow[]>([]);
  const [package_names, set_package_names] = useState<Record<string, string>>({});
  const [error, set_error] = useState('');
  const [form, set_form] = useState(() => initial_form_state(student_uuid));
  // Non-null while editing: the live override this form will supersede.
  const [editing_id, set_editing_id] = useState<number | null>(null);
  // Non-null while a delete is awaiting confirmation: the override the reason box belongs to.
  const [delete_target, set_delete_target] = useState<number | null>(null);
  const [delete_reason, set_delete_reason] = useState('');

  const sub_info: SubscriptionInfo = student_details?.subscription_info || {};
  const current_plan = student_details?.subscription_package_name || '—';
  const next_transaction_date =
    student_details?.next_transaction_date ||
    sub_info?.my_account_membership?.membership_renew_date;
  const next_prepaid_date = sub_info?.prepaid_next_charge || '';
  // "Allowed" applies only to old Consistency plans; the stored category decides, the flag is the fallback (PROD-9606).
  const is_consistency_plan = sub_info?.plan_category
    ? sub_info.plan_category === 'consistency'
    : Number(sub_info?.is_high_volume) === 1;
  // A monthly member has no prepaid_members row to read the cycle from, so the field arrives as 0.
  // DISPLAY ONLY, here and in scope_weeks_label. The override's own no_of_weeks must stay 0/'' for
  // a monthly member: any value > 0 is a match condition and a monthly renewal's scope.no_of_weeks
  // is null (renewal_service.ts:2364), so storing 4 would produce a row that never applies.
  const enrolled_weeks =
    Number(student_details?.no_of_weeks_originally_enrolled_to) || MONTHLY_CYCLE_WEEKS;
  // String, not number: it seeds form.is_prepaid, and the form holds every tri-state as a string.
  const detected_is_prepaid: TriInput =
    sub_info?.is_prepaid === 1 || sub_info?.is_prepaid === '1'
      ? '1'
      : sub_info?.is_prepaid === 0 || sub_info?.is_prepaid === '0'
        ? '0'
        : '';

  const billing_label = (is_prepaid: TriState | TriInput) => {
    if (is_prepaid === '' || is_prepaid === null || is_prepaid === undefined) return 'Unknown';
    return Number(is_prepaid) === 1 ? 'Prepaid' : 'Monthly';
  };

  const load_overrides = async () => {
    set_loading(true);
    set_error('');
    try {
      const url = get_api_url('student_overrides/list');
      const response = await api_with_method('post', url, { student_uuid }, true);
      set_overrides(response?.data?.overrides || []);
    } catch (err: any) {
      set_error(err?.response?.data?.message || 'Failed to load overrides');
    } finally {
      set_loading(false);
    }
  };

  const load_package_names = async () => {
    try {
      const url = build_active_packages_url(get_api_url('active_packages'), student_uuid);
      const response = await get_api(url, true);
      const map: Record<string, string> = {};
      (response?.data?.packages || []).forEach(
        (pkg: { uuid: string; name?: string; description?: string }) => {
          map[pkg.uuid] = pkg.name || pkg.description || '';
        }
      );
      set_package_names(map);
    } catch {
      // Cosmetic: fall back to the uuid rather than block the form.
      set_package_names({});
    }
  };

  const package_label = (uuid?: string | null) => {
    if (!uuid) return 'Any plan';
    return package_names[uuid] || uuid;
  };

  const start_delete = (id: number) => {
    set_error('');
    set_delete_target(id);
    set_delete_reason('');
  };

  const cancel_delete = () => {
    set_delete_target(null);
    set_delete_reason('');
  };

  const reset_form = () => {
    set_editing_id(null);
    set_form({
      ...initial_form_state(student_uuid, detected_is_prepaid),
      package_uuid: current_package_uuid || '',
      currency: current_currency || '',
      // Read-only scope + provenance, seeded from the member's live prepaid_members row. A monthly
      // member has none, so both stay '' and go up as null/'' — an unscoped row that any cycle matches.
      no_of_weeks: sub_info?.prepaid_no_of_weeks ? String(sub_info.prepaid_no_of_weeks) : '',
      prepaid_members_uuid: sub_info?.prepaid_members_uuid || ''
    });
  };

  // Keeps the override's own scope so saving does not re-point it at today's plan.
  const start_edit = (override: OverrideRow) => {
    set_error('');
    // Edit and delete are mutually exclusive: leaving a half-typed delete reason open on a card
    // you have just started editing invites confirming the wrong one.
    cancel_delete();
    set_editing_id(override.id);
    set_form({
      student_uuid,
      type: override.type || 'renewal',
      package_uuid: override.package_uuid || '',
      currency: override.currency || '',
      is_prepaid:
        override.is_prepaid === null || override.is_prepaid === undefined
          ? ''
          : (String(override.is_prepaid) as TriInput),
      // ?? not ||: a stored 0 means "written for a monthly member, matches any cycle" and must
      // survive the edit, where || would blank it and re-scope the row to today's prepaid cycle.
      no_of_weeks:
        override.no_of_weeks === null || override.no_of_weeks === undefined
          ? ''
          : String(override.no_of_weeks),
      prepaid_members_uuid: override.prepaid_members_uuid || '',
      valid_from: to_date_input(override.valid_from),
      valid_until: to_date_input(override.valid_until),
      amount:
        override.amount === null || override.amount === undefined ? '' : String(override.amount),
      is_rollover_allowed:
        override.is_rollover_allowed === null || override.is_rollover_allowed === undefined
          ? ''
          : (String(override.is_rollover_allowed) as TriInput),
      ticket: override.ticket || '',
      reason: override.reason || ''
    });
  };

  useEffect(() => {
    load_overrides();
    load_package_names();
    reset_form();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student_uuid, current_currency, current_package_uuid, detected_is_prepaid]);

  const update_field = (key: keyof FormState, value: string) => {
    set_form((prev) => ({ ...prev, [key]: value }));
  };

  const handle_submit = async () => {
    // Mirrors OVERRIDE_TYPE_RULES on the API.
    const needs_price_and_scope = form.type === 'renewal';
    const needs_rate = form.type === 'recording';
    // Currency is no longer typeable, so a missing one is a data problem, not a blank field. The
    // generic message below would leave ops staring at a form they cannot complete.
    if ((needs_price_and_scope || needs_rate) && !form.currency) {
      set_error(
        'No billing currency found on this subscription, so the override cannot be scoped. Check the plan before setting a price.'
      );
      return;
    }
    if (
      !form.type ||
      !form.ticket ||
      !form.reason ||
      (needs_price_and_scope && (!form.amount || !form.package_uuid || !form.currency)) ||
      (needs_rate && (!form.amount || !form.currency))
    ) {
      set_error('Please fill all required fields.');
      return;
    }

    set_saving(true);
    set_error('');
    try {
      const url = get_api_url(editing_id ? 'student_overrides/update' : 'student_overrides/create');
      const payload = {
        ...form,
        ...(editing_id ? { id: editing_id } : {}),
        amount: Number(form.amount),
        valid_from: form.type === 'renewal' ? '' : to_iso(form.valid_from),
        valid_until: form.type === 'renewal' ? '' : to_iso(form.valid_until),
        is_rollover_allowed:
          form.is_rollover_allowed === '' ? null : Number(form.is_rollover_allowed),
        is_prepaid: form.is_prepaid === '' ? null : Number(form.is_prepaid),
        // '' would fail the API's joi.number(); null is how "not scoped by this" travels.
        // prepaid_members_uuid goes as-is — joi allows '' on it.
        no_of_weeks: form.no_of_weeks === '' ? null : Number(form.no_of_weeks)
      };
      await api_with_method('post', url, payload, true);
      set_notification_variable(
        true,
        'success',
        editing_id ? 'Override updated — the previous one is kept as history' : 'Override created'
      );
      reset_form();
      await load_overrides();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (editing_id ? 'Failed to update override' : 'Failed to create override');
      set_error(msg);
      set_notification_variable(true, 'error', msg);
    } finally {
      set_saving(false);
    }
  };

  // Deleting deactivates a live price override, which changes what the member is charged on their
  // next renewal — hence the confirm step rather than a one-click action next to Edit.
  // The reason is typed, not canned: cms_service.delete_student_override writes it to the timeline
  // event and not to the row, so it is the only record of why the price went away.
  const handle_delete = async () => {
    const id = delete_target;
    const reason = delete_reason.trim();
    if (!id || !reason) return;
    set_saving(true);
    set_error('');
    try {
      const url = get_api_url('student_overrides/delete');
      await api_with_method('post', url, { id, reason }, true);
      set_notification_variable(true, 'success', 'Override deleted');
      if (editing_id === id) reset_form();
      cancel_delete();
      await load_overrides();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete override';
      set_error(msg);
      set_notification_variable(true, 'error', msg);
    } finally {
      set_saving(false);
    }
  };

  const format_money = (amount?: number | string | null, symbol?: string, code?: string) => {
    if (amount === null || amount === undefined || amount === '') return '—';
    const value = Number(amount);
    if (!Number.isFinite(value)) return '—';
    return `${symbol || ''}${value.toFixed(2)}${symbol ? '' : ` ${code || ''}`}`.trim();
  };

  // Plan name and billing mode head the panel instead of sitting in here: a plan slug is long
  // enough to truncate into uselessness inside an equal-width stat cell.
  // The same four cells in the same order for every member, monthly and prepaid alike. Rendering
  // the prepaid cell only when it had a date used to shift "Weeks enrolled" between column 3 and
  // column 4, so support read a different layout per student. format_date already prints an absent
  // date as an em dash, which is how a monthly member's prepaid cell reads.
  const summary_items = [
    {
      label: 'Charged today',
      value: format_money(sub_info?.purchase_price, sub_info?.currency_symbol, sub_info?.currency)
    },
    { label: 'Next renewal', value: format_date(next_transaction_date) },
    { label: 'Next prepaid charge', value: format_date(next_prepaid_date) },
    { label: 'Weeks enrolled', value: `${enrolled_weeks} weeks` }
  ];

  const live_overrides = overrides.filter(is_live);
  const superseded_overrides = overrides.filter((override: OverrideRow) => !is_live(override));

  const submit_label = editing_id ? 'Save changes' : 'Create override';

  const describe_validity = (override: OverrideRow) => {
    const from = override.valid_from ? format_date(override.valid_from) : '';
    const until = override.valid_until ? format_date(override.valid_until) : '';
    if (from && until) return `${from} → ${until}`;
    if (until) return `until ${until}`;
    if (from) return `from ${from}`;
    return override.type === 'renewal' ? 'until deleted' : 'no end date';
  };

  const status_pill = (override: OverrideRow) =>
    is_live(override) ? (
      <span
        className={`${chip_class} shrink-0 gap-1 bg-state-success-bg font-semibold text-state-success-text`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-state-success" />
        Active
      </span>
    ) : (
      <span className={`${chip_class} shrink-0 bg-surface-card text-content-muted`}>Inactive</span>
    );

  // Every condition that decides whether this row applies, one chip each. Rollover-blocked gets a
  // warning tint because it is the only one that changes money rather than just narrowing scope.
  const scope_chips = (override: OverrideRow) => {
    const chips = [
      { text: scope_billing_label(override.is_prepaid), tone: 'bg-surface-sunken' },
      {
        text: scope_weeks_label(override.no_of_weeks, override.is_prepaid),
        tone: 'bg-surface-sunken'
      },
      { text: describe_validity(override), tone: 'bg-surface-sunken' }
    ];
    if (override.is_rollover_allowed !== null && override.is_rollover_allowed !== undefined) {
      chips.push(
        Number(override.is_rollover_allowed) === 1
          ? { text: 'rollover allowed', tone: 'bg-surface-sunken' }
          : { text: 'rollover blocked', tone: 'bg-state-warning-bg text-state-warning-text' }
      );
    }
    return chips;
  };

  // Shared by live and past cards, so the two read identically apart from the status pill.
  const render_override_body = (override: OverrideRow) => (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {override.amount === null || override.amount === undefined ? (
            <div className="text-sm font-semibold text-content-primary">
              {type_label(override.type)}
            </div>
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-base font-semibold text-content-primary">
                {override.amount}
              </span>
              {/* Stored on the row and shown on live and past cards alike: it is what the
                  amount is denominated in, and a scope condition in its own right. */}
              <span className="text-[11px] font-semibold uppercase text-content-secondary">
                {override.currency || ''}
              </span>
              <span className="text-[11px] text-content-muted">{type_label(override.type)}</span>
            </div>
          )}
          {/* Plan slugs overflow a 20rem column, so truncate and keep the full name on hover. */}
          <div
            className="mt-1 truncate text-xs text-content-secondary"
            title={package_label(override.package_uuid)}
          >
            {package_label(override.package_uuid)}
          </div>
        </div>
        {status_pill(override)}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {scope_chips(override).map((chip) => (
          <span key={chip.text} className={`${chip_class} ${chip.tone}`}>
            {chip.text}
          </span>
        ))}
      </div>
      <div className="mt-2 break-words text-xs leading-relaxed text-content-muted">
        <span className="font-semibold text-content-secondary">{override.ticket}</span> —{' '}
        {override.reason}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div className="rounded-xl border border-line bg-surface-sunken px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-base font-semibold text-content-primary">{current_plan}</span>
          <span className={`${chip_class} bg-surface-card`}>
            {billing_label(detected_is_prepaid)}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-3 sm:grid-cols-4">
          {summary_items.map((item) => (
            <div key={item.label} className="min-w-0">
              <div className={stat_label_class}>{item.label}</div>
              <div
                className="mt-0.5 truncate text-sm font-semibold text-content-primary"
                title={String(item.value)}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          <h3 className={`${section_title_class} mb-3`}>
            {editing_id ? `Edit override #${editing_id}` : 'New override'}
          </h3>
          {/* A brand tint rather than a border accent: mixing `border` with `border-l-2` leaves the
              winning border-width up to Tailwind's output order. */}
          {editing_id ? (
            <div className="mb-4 rounded-lg bg-[var(--brand-primary-soft)] px-3.5 py-2.5 text-xs leading-relaxed text-content-secondary">
              Saving marks override #{editing_id} inactive and creates a new one with these values.
              The old row stays on record so past charges can still be explained.
            </div>
          ) : null}
          <div className="grid gap-x-4 gap-y-4 sm:grid-cols-3">
            <label className="text-sm">
              <span className={label_class}>Type *</span>
              <select
                className={field_class}
                value={form.type}
                disabled={!!editing_id}
                onChange={(e) => update_field('type', e.target.value)}
              >
                <option value="renewal">Renewal</option>
                {/* Recording access is off until goserver reads student_override (PROD-8053):
                    a row would bill for access that is still blocked server-side.
                    <option value="recording">Recording access (per-session rate)</option> */}
              </select>
            </label>
            {/* Amount is the only editable half. Currency is scope, not a choice: an override only
                applies while the member is billed in it (override_mismatch checks row.currency
                against the renewal's), so letting ops type one stored a row that could never match.
                It stays in `form` and still posts, so student_override.currency is written as
                before -- readOnly removes the input, not the value. */}
            <label className="text-sm">
              <span className={label_class}>
                Amount
                {form.type === 'renewal' || form.type === 'recording' ? ' *' : ''}
              </span>
              <div className="num-input-no-arr flex items-center rounded-lg border border-line bg-surface-card transition-colors focus-within:border-brand-primary">
                <input
                  className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-content-primary shadow-none focus:outline-none"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => update_field('amount', e.target.value)}
                />
                {/* readOnly, not disabled: a disabled input is skipped by form serialisation and
                    greys out, while readOnly stays readable and copyable. React does not warn
                    about a value with no onChange as long as readOnly is set. */}
                <input
                  className="w-14 shrink-0 cursor-default border-0 bg-transparent py-2 pr-3 text-right text-xs font-semibold uppercase text-content-muted shadow-none focus:outline-none"
                  value={form.currency}
                  readOnly
                  title={
                    editing_id
                      ? 'Billing currency, kept from the override being edited'
                      : "The member's billing currency — the override stops applying if it changes"
                  }
                />
              </div>
            </label>
            {/* Renewal has no validity window (delete is its only expiry), so the third cell is
                rollover; every other type gets the window instead. No spacer either way. */}
            {form.type === 'renewal' ? (
              <label className="text-sm">
                <span className={label_class}>Rollover</span>
                <select
                  className={field_class}
                  value={form.is_rollover_allowed}
                  onChange={(e) => update_field('is_rollover_allowed', e.target.value)}
                >
                  <option value="">As calculated</option>
                  <option value="1" disabled={!is_consistency_plan}>
                    Allowed{is_consistency_plan ? '' : ' — Consistency plans only'}
                  </option>
                  <option value="0">Blocked (0 hrs)</option>
                </select>
              </label>
            ) : (
              <>
                <label className="text-sm">
                  <span className={label_class}>Valid from</span>
                  <input
                    className={field_class}
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => update_field('valid_from', e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className={label_class}>Valid until</span>
                  <input
                    className={field_class}
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => update_field('valid_until', e.target.value)}
                  />
                </label>
              </>
            )}
            <label className="text-sm">
              <span className={label_class}>Ticket *</span>
              <input
                className={field_class}
                value={form.ticket}
                onChange={(e) => update_field('ticket', e.target.value)}
                placeholder="PROD-8053"
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className={label_class}>Reason *</span>
              <textarea
                className={`${field_class} min-h-[76px]`}
                value={form.reason}
                onChange={(e) => update_field('reason', e.target.value)}
                placeholder="Why is this override needed?"
              />
            </label>
          </div>

          {/* <div>, not <p>: index.css gives every <p> a 1em block margin that fights this grid.
              Same chips as the override cards, so what you are about to create reads like what is
              already stored. */}
          <div className="mt-4 rounded-lg bg-surface-sunken px-3.5 py-3">
            <div className={stat_label_class}>Scope</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <span className={`${chip_class} bg-surface-card`}>
                {package_label(form.package_uuid)}
              </span>
              <span className={`${chip_class} bg-surface-card`}>
                {billing_label(form.is_prepaid).toLowerCase()} billing
              </span>
              <span className={`${chip_class} bg-surface-card`}>
                {scope_weeks_label(form.no_of_weeks, form.is_prepaid)}
              </span>
              {form.currency ? (
                <span className={`${chip_class} bg-surface-card`}>billed in {form.currency}</span>
              ) : null}
            </div>
            <div className="mt-2 text-xs leading-relaxed text-content-muted">
              {editing_id
                ? 'Kept from the override being edited and not editable here.'
                : 'Taken from their current plan and not editable here.'}{' '}
              The override stops applying if they move off it.
            </div>
          </div>

          {error ? (
            <div className="mt-4 rounded-lg bg-state-error-bg px-3.5 py-2.5 text-sm leading-relaxed text-state-error-text">
              {error}
            </div>
          ) : null}
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className={section_title_class}>
              Existing overrides
              {live_overrides.length ? ` (${live_overrides.length})` : ''}
            </h3>
            <button
              type="button"
              onClick={load_overrides}
              disabled={loading}
              className={`${ghost_button_class} text-brand-primary hover:underline`}
            >
              Refresh
            </button>
          </div>

          {loading ? <CustomLoading /> : null}
          {!loading && live_overrides.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line px-3.5 py-5 text-center text-xs leading-relaxed text-content-muted">
              No active override.
              <br />
              This member is on their normal pricing.
            </div>
          ) : null}
          {!loading && live_overrides.length > 0 ? (
            <div className="flex flex-col gap-2">
              {live_overrides.map((override) => (
                <div
                  key={override.id}
                  className={`rounded-xl border px-3.5 py-3 transition-colors ${
                    editing_id === override.id
                      ? 'border-brand-primary bg-[var(--brand-primary-soft)]'
                      : 'border-line bg-surface-card'
                  }`}
                >
                  {render_override_body(override)}
                  {/* Actions sit under a divider rather than in a right-hand column: at 20rem the
                      stacked links squeezed the amount and plan name into two words a line. */}
                  <div className="mt-2.5 border-t border-line pt-2.5">
                    {delete_target === override.id ? (
                      <div className="flex flex-col gap-2">
                        <div className="text-xs leading-relaxed text-state-error">
                          This stops the override applying. The member goes back to standard pricing
                          from their next renewal.
                        </div>
                        <input
                          type="text"
                          className={field_class}
                          value={delete_reason}
                          onChange={(e) => set_delete_reason(e.target.value)}
                          placeholder="Reason for deleting *"
                        />
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={handle_delete}
                            disabled={saving || !delete_reason.trim()}
                            className={`${ghost_button_class} text-state-error hover:underline`}
                          >
                            {saving ? 'Deleting...' : 'Confirm delete'}
                          </button>
                          <button
                            type="button"
                            onClick={cancel_delete}
                            disabled={saving}
                            className={`${ghost_button_class} text-content-secondary hover:underline`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => start_edit(override)}
                          disabled={saving}
                          className={`${ghost_button_class} text-brand-primary hover:underline`}
                        >
                          {editing_id === override.id ? 'Editing' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          onClick={() => start_delete(override.id)}
                          disabled={saving}
                          className={`${ghost_button_class} text-state-error hover:underline`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* The body's own "Inactive" pill says these no longer apply, so no status line here.
              superseded_by_id stays out of the UI: a raw row id means nothing to ops, and the
              edit-vs-delete distinction it encodes is still queryable in the DB. */}
          {!loading && superseded_overrides.length > 0 ? (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-medium text-content-muted hover:text-content-secondary">
                Past overrides ({superseded_overrides.length})
              </summary>
              <div className="mt-2.5 flex flex-col gap-2">
                {superseded_overrides.map((override) => (
                  <div
                    key={override.id}
                    className="rounded-xl border border-dashed border-line bg-surface-sunken px-3.5 py-3 opacity-75"
                  >
                    {render_override_body(override)}
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      </div>

      <hr className="-mx-6 mt-7 mb-4 border-line" />
      <div className="flex justify-end gap-3">
        <Button type="secondary" onClick={editing_id ? reset_form : on_close}>
          {editing_id ? 'Cancel edit' : 'Close'}
        </Button>
        <Button onClick={handle_submit} disabled={saving}>
          {saving ? 'Saving...' : submit_label}
        </Button>
      </div>
    </div>
  );
}

export default StudentOverrides;
